import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PlayerForm from './PlayerForm';
import ImportPlayersModal from './ImportPlayersModal';
import { playersService } from '../../../services/supabaseService';

const TeamPlayersPanel = ({ team, onClose, onRefresh }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    fetchPlayers();
  }, [team?.id]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const data = await playersService?.getByTeam(team?.id);
      setPlayers(data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = async (playerData) => {
    try {
      await playersService?.create(playerData);
      await fetchPlayers();
      setIsAddingPlayer(false);
      onRefresh?.();
    } catch (error) {
      console.error('Error adding player:', error);
      throw error;
    }
  };

  const handleImportPlayers = async (playersData) => {
    try {
      for (const playerData of playersData) {
        await playersService?.create(playerData);
      }
      await fetchPlayers();
      onRefresh?.();
    } catch (error) {
      console.error('Error importing players:', error);
      throw error;
    }
  };

  const handleUpdatePlayer = async (playerData) => {
    try {
      await playersService?.update(editingPlayer?.id, playerData);
      await fetchPlayers();
      setEditingPlayer(null);
      onRefresh?.();
    } catch (error) {
      console.error('Error updating player:', error);
      throw error;
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!confirm('Are you sure you want to delete this player?')) {
      return;
    }

    try {
      await playersService?.delete(playerId);
      await fetchPlayers();
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting player:', error);
      setToastMsg('Failed to delete player');
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Batter': case'batsman':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Bowler': case'bowler':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'All-rounder': case'all-rounder':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'Wicket Keeper': case'wicket-keeper':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1200]">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground">Team Players</h2>
              <p className="text-sm text-muted-foreground mt-1">{team?.name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                onClick={() => setShowImportModal(true)}>
                Import CSV
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsAddingPlayer(true)}>
                Add Player
              </Button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Icon name="X" className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading players...</p>
            </div>
          ) : players?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No players added yet</p>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsAddingPlayer(true)}>
                Add First Player
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {players?.map((player) => (
                <div
                  key={player?.id}
                  className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{player?.name}</h3>
                        {player?.jerseyNumber && (
                          <span className="text-xs font-medium text-muted-foreground">#{player?.jerseyNumber}</span>
                        )}
                      </div>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(player?.role)}`}>
                        {player?.role?.replace('-', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingPlayer(player)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Edit Player">
                        <Icon name="Edit" className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDeletePlayer(player?.id)}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        title="Delete Player">
                        <Icon name="Trash2" className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="Target" className="w-3 h-3" />
                      <span>Batting: {player?.battingStyle?.replace('-', ' ')}</span>
                    </div>
                    {player?.bowlingStyle && player?.bowlingStyle !== 'none' && (
                      <div className="flex items-center gap-2">
                        <Icon name="Zap" className="w-3 h-3" />
                        <span>Bowling: {player?.bowlingStyle?.replace('-', ' ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full">
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Add Player Modal */}
      {isAddingPlayer && (
        <PlayerForm
          player={null}
          teamId={team?.id}
          onSubmit={handleAddPlayer}
          onCancel={() => setIsAddingPlayer(false)}
        />
      )}

      {/* Edit Player Modal */}
      {editingPlayer && (
        <PlayerForm
          player={editingPlayer}
          teamId={team?.id}
          onSubmit={handleUpdatePlayer}
          onCancel={() => setEditingPlayer(null)}
        />
      )}

      {/* Import Players Modal */}
      {showImportModal && (
        <ImportPlayersModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportPlayers}
          teamId={team?.id}
          teamName={team?.name}
        />
      )}
      {toastMsg && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-destructive text-destructive-foreground px-4 py-3 rounded-lg shadow-lg text-sm font-medium">
          {toastMsg}
        </div>
      )}
    </div>
  );
};

export default TeamPlayersPanel;